/**
 * The arcade high-score table — pure rules, no storage, no DOM (owner slice,
 * 2026-07-29: "enter your initials after the run and it's added to the ledger
 * and displayed as a high score list", explicitly WITHOUT accounts/auth).
 *
 * This is the FAMILY ARCADE list: per-seed, honor-system, unranked. It is
 * deliberately NOT milestone three's validated ranked Daily leaderboard
 * (LOCKED-DESIGN 15.2) — that one requires passkey identity, attempt tokens
 * and the submission-boundary hardening recorded in HANDOFF, and none of it is
 * built here. Keeping this module pure means the storage seam (same-origin
 * /api/scores on the deployed Worker, localStorage everywhere else) carries no
 * rules, so both backends behave identically and the rules are unit-pinned.
 *
 * PER-SEED tables, always: Simple First Shift tops out near ~55k while a
 * ranked seed scores ~540k — one mixed list would bury every kid entry under
 * adult runs within a day. The seed id is the table key.
 */

/** One ledger row. `ts` is stamped by the caller (storage layer), never here. */
export interface HighScoreEntry {
  /** 1-3 chars, A-Z 0-9 only — sanitizeInitials is the single gate. */
  initials: string;
  score: number;
  /** epoch ms; used only for stable ordering among equal scores (older wins). */
  ts: number;
}

/** Rows shown on screen. */
export const TABLE_DISPLAY_SIZE = 10;
/** Rows retained in the ledger (headroom so a display resize needs no migration). */
export const TABLE_RETAIN_SIZE = 50;
/** Arcade convention: three letters. */
export const INITIALS_MAX = 3;
/** The classic arcade alphabet the selector offers, in selector order. */
export const INITIALS_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

/**
 * Sanity ceiling for a submitted score. This is NOT anti-cheat (the arcade
 * list is honor-system by scope) — it is a corruption guard so a garbage
 * payload cannot render a 12-digit banner over the kids' table. The three
 * frozen ranked fixtures land ~527k-541k; 2,000,000 leaves generous headroom
 * above any real run while rejecting nonsense.
 */
export const SCORE_SANITY_CAP = 2_000_000;

/**
 * The single initials gate, shared by the entry UI and both storage backends:
 * uppercase, strip everything outside the arcade alphabet, cap at three.
 * Empty after stripping falls back to "AAA" rather than an empty row — a
 * ledger row must always render.
 */
export function sanitizeInitials(raw: string): string {
  const cleaned = raw.toUpperCase().split("").filter((ch) => INITIALS_ALPHABET.includes(ch)).join("");
  return (cleaned || "AAA").slice(0, INITIALS_MAX);
}

/** A score is storable when it is a real, positive, sane number. */
export function isSaneScore(score: unknown): score is number {
  return typeof score === "number" && Number.isFinite(score) && score > 0 && score <= SCORE_SANITY_CAP;
}

/**
 * Parse an untrusted ledger (localStorage blob or Worker response) into a
 * well-formed table: shape-checked, sanitized, sorted, capped. Garbage rows
 * are dropped, never repaired into surprising positions. This is the ONE
 * normalization path — both backends and the renderer go through it, so a
 * corrupt blob can degrade to a shorter list but never to a broken screen.
 */
export function normalizeTable(raw: unknown): HighScoreEntry[] {
  if (!Array.isArray(raw)) return [];
  const rows: HighScoreEntry[] = [];
  for (const row of raw) {
    if (typeof row !== "object" || row === null) continue;
    const candidate = row as Record<string, unknown>;
    if (!isSaneScore(candidate["score"])) continue;
    if (typeof candidate["initials"] !== "string") continue;
    const ts = typeof candidate["ts"] === "number" && Number.isFinite(candidate["ts"]) && candidate["ts"] >= 0 ? candidate["ts"] : 0;
    rows.push({ initials: sanitizeInitials(candidate["initials"]), score: Math.floor(candidate["score"] as number), ts });
  }
  return sortTable(rows).slice(0, TABLE_RETAIN_SIZE);
}

/** Highest score first; equal scores keep the OLDER entry above (arcade honor). */
function sortTable(rows: HighScoreEntry[]): HighScoreEntry[] {
  return [...rows].sort((a, b) => (b.score - a.score) || (a.ts - b.ts));
}

/**
 * Would this score make the DISPLAYED table? This is the "enter your
 * initials" gate: arcade machines only ask when you made the board. While the
 * table has open display slots, any sane score qualifies.
 */
export function qualifies(table: readonly HighScoreEntry[], score: number): boolean {
  if (!isSaneScore(score)) return false;
  if (table.length < TABLE_DISPLAY_SIZE) return true;
  // Beat the last DISPLAYED row; ties don't displace (the incumbent is older).
  return score > table[TABLE_DISPLAY_SIZE - 1]!.score;
}

/**
 * Insert an entry and return the new table plus the entry's display rank
 * (1-based; null when it landed off the displayed board). Pure — callers own
 * persistence and the timestamp.
 */
export function insertEntry(
  table: readonly HighScoreEntry[],
  entry: HighScoreEntry,
): { table: HighScoreEntry[]; rank: number | null } {
  const inserted = { ...entry, initials: sanitizeInitials(entry.initials) };
  const next = sortTable([...table, inserted]).slice(0, TABLE_RETAIN_SIZE);
  const index = next.findIndex((row) => row.initials === inserted.initials && row.score === inserted.score && row.ts === inserted.ts);
  const rank = index >= 0 && index < TABLE_DISPLAY_SIZE ? index + 1 : null;
  return { table: next, rank };
}

/**
 * Merge two ledgers (e.g. a local fallback list with the shared one once the
 * backend is reachable again): union, dedupe exact rows, re-sort, cap. Keeps
 * the operation idempotent so repeated merges cannot grow the table.
 */
export function mergeTables(a: readonly HighScoreEntry[], b: readonly HighScoreEntry[]): HighScoreEntry[] {
  const seen = new Set<string>();
  const union: HighScoreEntry[] = [];
  for (const row of [...a, ...b]) {
    const key = `${row.initials}|${row.score}|${row.ts}`;
    if (seen.has(key)) continue;
    seen.add(key);
    union.push(row);
  }
  return sortTable(union).slice(0, TABLE_RETAIN_SIZE);
}
